/**
 * Add pages from elsewhere: copy pages out of another report of this workspace, or import a file (PowerPoint,
 * PDF, pictures, deck JSON). Both end in the same page picker: tick the pages, add them after the current one.
 */
import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check, FileUp, Loader2, Search, X } from 'lucide-react';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { deriveTheme, type Theme } from '@/cowork/omniad/components/slides/SlideRenderer';
import { newId } from '@/cowork/omniad/components/slides/elements';
import { SlideThumb } from '../SlideThumb';
import { importDeckFiles, IMPORT_ACCEPT, IMPORT_FORMATS, type ImportResult } from './importSlides';

type Slide = Record<string, any>;
export type ImportSource = 'deck' | 'file';

/** A page ready to live in another deck: no build plan, fresh element ids. */
export function cleanCopy(slide: Slide): Slide {
  const s: Slide = JSON.parse(JSON.stringify(slide));
  delete s._plan; delete s._pending;
  if (Array.isArray(s.elements)) s.elements = s.elements.map((e: any) => ({ ...e, id: newId() }));
  return s;
}

export function ImportPagesDialog({ initial, currentId, theme, title, onClose, onAdd }: {
  initial: ImportSource; currentId: string; theme: Theme; title: string;
  onClose: () => void; onAdd: (slides: Slide[], from: string) => void;
}) {
  const [tab, setTab] = useState<ImportSource>(initial);
  const [pages, setPages] = useState<{ from: string; slides: Slide[]; skipped: string[] } | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const show = (from: string, slides: Slide[], skipped: string[] = [], preselect = false) => { setPages({ from, slides, skipped }); setPicked(new Set(preselect ? slides.map((_, i) => i) : [])); };

  const add = () => {
    if (!pages) return;
    const list = pages.slides.filter((_, i) => picked.has(i)).map(cleanCopy);
    if (list.length) onAdd(list, pages.from);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#101828]/40 p-6" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center gap-3 border-b border-[#eaecf0] px-5 py-3">
          {pages && <button onClick={() => setPages(null)} className="rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7]" title="Back"><ArrowLeft className="h-4 w-4" /></button>}
          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] font-semibold text-[#101828]">{pages ? pages.from : 'Add pages from…'}</div>
            {pages && <div className="text-[12px] text-[#667085]">Pick the pages to add after the current one. They take this report's look where they use the brand colours.</div>}
          </div>
          {!pages && (
            <div className="flex rounded-lg bg-[#f2f4f7] p-0.5 text-[12.5px] font-medium">
              {(['deck', 'file'] as const).map((t) => (
                <button key={t} onClick={() => setTab(t)} className={`rounded-md px-3 py-1.5 ${tab === t ? 'bg-white text-[#101828] shadow-sm' : 'text-[#667085]'}`}>{t === 'deck' ? 'Another report' : 'A file'}</button>
              ))}
            </div>
          )}
          <button onClick={onClose} className="rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7]" title="Close"><X className="h-4 w-4" /></button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {pages
            ? <PagePicker slides={pages.slides} skipped={pages.skipped} picked={picked} setPicked={setPicked} theme={theme} title={title} />
            : tab === 'deck'
              ? <DeckList currentId={currentId} onOpen={(from, slides) => show(from, slides)} />
              : <FileDrop onDone={(from, r) => show(from, r.slides, r.skipped, true)} />}
        </div>

        {pages && (
          <div className="flex items-center gap-3 border-t border-[#eaecf0] px-5 py-3">
            <button onClick={() => setPicked(picked.size === pages.slides.length ? new Set() : new Set(pages.slides.map((_, i) => i)))} className="text-[12.5px] font-medium text-[#7a1fbf] hover:underline">
              {picked.size === pages.slides.length ? 'Clear selection' : 'Select all'}
            </button>
            <div className="flex-1 text-right text-[12.5px] text-[#667085]">{picked.size} of {pages.slides.length} selected</div>
            <button onClick={add} disabled={!picked.size} className="rounded-lg bg-[#be43ff] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#a835e0] disabled:opacity-40">
              Add {picked.size || ''} page{picked.size === 1 ? '' : 's'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PagePicker({ slides, skipped, picked, setPicked, theme, title }: { slides: Slide[]; skipped: string[]; picked: Set<number>; setPicked: (s: Set<number>) => void; theme: Theme; title: string }) {
  const toggle = (i: number) => { const n = new Set(picked); if (n.has(i)) n.delete(i); else n.add(i); setPicked(n); };
  return (
    <>
      {skipped.length > 0 && <div className="mb-3 rounded-lg bg-[#fffaeb] px-3 py-2 text-[12px] text-[#93370d]">Left out: {skipped.join(', ')}.</div>}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {slides.map((s, i) => {
          const on = picked.has(i);
          return (
            <button key={i} onClick={() => toggle(i)} className={`relative overflow-hidden rounded-lg border text-left ${on ? 'border-[#be43ff] ring-2 ring-[#be43ff]/25' : 'border-[#eaecf0] hover:border-[#d0d5dd]'}`}>
              <SlideThumb slide={s} index={i} theme={theme} title={title} />
              <div className="flex items-center justify-between px-2 py-1 text-[11px] text-[#667085]"><span>Page {i + 1}</span></div>
              <span className={`absolute left-2 top-2 grid h-5 w-5 place-items-center rounded-md border ${on ? 'border-[#be43ff] bg-[#be43ff] text-white' : 'border-[#d0d5dd] bg-white/90'}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

function DeckList({ currentId, onOpen }: { currentId: string; onOpen: (from: string, slides: Slide[]) => void }) {
  const [q, setQ] = useState('');
  const [opening, setOpening] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['cowork', 'presentations'], queryFn: () => coworkApi.presentations.list() });
  const rows = useMemo(() => {
    const all = (list.data?.presentations || []).filter((p) => p.id !== currentId && p.generation?.status !== 'running');
    const needle = q.trim().toLowerCase();
    return (needle ? all.filter((p) => p.title.toLowerCase().includes(needle)) : all).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 60);
  }, [list.data, q, currentId]);
  const open = async (id: string, name: string) => {
    setOpening(id); setErr(null);
    try {
      const d = await coworkApi.presentations.get(id);
      const slides = ((d.slides as Slide[]) || []).filter((s) => !s?._plan);
      if (!slides.length) { setErr(`"${name}" has no finished pages to copy.`); return; }
      onOpen(name, slides);
    } catch (e) { setErr((e as Error).message); } finally { setOpening(null); }
  };
  return (
    <>
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#98a2b3]" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this workspace's reports" className="w-full rounded-lg border border-[#d0d5dd] py-2 pl-9 pr-3 text-[13px] outline-none focus:border-[#be43ff]" />
      </div>
      {err && <div className="mb-3 rounded-lg bg-[#fef3f2] px-3 py-2 text-[12px] text-[#b42318]">{err}</div>}
      {list.isPending ? <div className="flex items-center gap-2 py-8 text-[13px] text-[#667085]"><Loader2 className="h-4 w-4 animate-spin" /> Loading reports…</div>
        : !rows.length ? <div className="py-8 text-center text-[13px] text-[#98a2b3]">{q ? 'No report matches.' : 'No other reports in this workspace yet.'}</div>
        : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {rows.map((p) => (
              <button key={p.id} onClick={() => void open(p.id, p.title)} disabled={!!opening} className="overflow-hidden rounded-lg border border-[#eaecf0] text-left hover:border-[#be43ff] disabled:opacity-60">
                <CoverThumb cover={p.cover} brand={p.brand} title={p.title} />
                <div className="px-2.5 py-2">
                  <div className="flex items-center gap-1.5 text-[12.5px] font-medium text-[#101828]"><span className="truncate">{p.title}</span>{opening === p.id && <Loader2 className="h-3 w-3 shrink-0 animate-spin text-[#be43ff]" />}</div>
                  <div className="text-[11px] text-[#98a2b3]">{p.slideCount ?? '—'} pages · {new Date(p.updatedAt).toLocaleDateString()}</div>
                </div>
              </button>
            ))}
          </div>
        )}
    </>
  );
}

function CoverThumb({ cover, brand, title }: { cover?: Record<string, unknown> | null; brand?: Record<string, unknown>; title: string }) {
  const theme = useMemo(() => deriveTheme((brand || {}) as Parameters<typeof deriveTheme>[0]), [brand]);
  return cover ? <SlideThumb slide={cover} index={0} theme={theme} title={title} /> : <div className="aspect-[16/9] bg-[#f2f4f7]" />;
}

/** Drop or choose files; they are read (and their pictures uploaded) into pages. */
export function FileDrop({ onDone }: { onDone: (from: string, result: ImportResult) => void | Promise<void> }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const run = async (files: File[]) => {
    if (!files.length) return;
    setErr(null); setBusy('Reading…');
    try {
      const r = await importDeckFiles(files, { onProgress: setBusy });
      await onDone(files.length === 1 ? files[0].name : `${files.length} files`, r);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) void run(Array.from(e.dataTransfer.files)); }}
      className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-14 text-center ${over ? 'border-[#be43ff] bg-[#fcf7ff]' : 'border-[#d0d5dd]'}`}
    >
      {busy ? (
        <><Loader2 className="mb-3 h-7 w-7 animate-spin text-[#be43ff]" /><div className="text-[13.5px] font-medium text-[#101828]">{busy}</div></>
      ) : (
        <>
          <FileUp className="mb-3 h-7 w-7 text-[#be43ff]" />
          <div className="text-[14px] font-semibold text-[#101828]">Drop a file here, or <button onClick={() => input.current?.click()} className="text-[#7a1fbf] underline">choose one</button></div>
          <div className="mt-1 max-w-md text-[12.5px] text-[#667085]">{IMPORT_FORMATS}. PowerPoint pages stay editable (text, pictures, shapes, tables, charts); PDF pages come in as pictures.</div>
          <input ref={input} type="file" multiple accept={IMPORT_ACCEPT} className="hidden" onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ''; void run(f); }} />
        </>
      )}
      {err && <div className="mt-4 rounded-lg bg-[#fef3f2] px-3 py-2 text-[12px] text-[#b42318]">{err}</div>}
    </div>
  );
}
